import { Link } from 'react-router-dom';
import { describeSubscriptionState, type NoticeTone } from './stateNotice';
import { useCurrentSubscription } from './useCurrentSubscription';
import './billing.css';

const TONE_GLYPH: Record<NoticeTone, string> = { info: 'ℹ', warning: '⚠', danger: '⛔' };
const TONE_LABEL: Record<NoticeTone, string> = { info: 'Notice', warning: 'Warning', danger: 'Action needed' };

/**
 * EPN-105 — the site-wide subscription banner, rendered by `AppShell` under the topbar on every authenticated
 * screen. Driven ONLY by the server's subscription fields through `describeSubscriptionState` — it never reads the
 * browser clock and never guesses:
 *  - while the state is unknown (loading, or the lookup failed) nothing is shown, so a banner is never a guess;
 *  - a healthy paid subscription shows nothing.
 * Billing pages stay reachable in every state, and every state here links to them. The tone glyph and the label text
 * carry the meaning, so colour is never the only signal.
 */
export function SubscriptionBanner() {
  const { subscription, loading, error } = useCurrentSubscription();

  if (error) return null;
  if (loading && subscription === null) return null;

  const notice = describeSubscriptionState(subscription);
  if (!notice) return null;

  return (
    <section
      className={`sub-banner sub-banner--${notice.tone}`}
      aria-label="Plan and billing notice"
      aria-live="polite"
      data-testid="subscription-banner"
      data-notice={notice.id}
    >
      <span className="sub-banner__glyph" aria-hidden="true">{TONE_GLYPH[notice.tone]}</span>
      <div className="sub-banner__body">
        <p className="sub-banner__title">
          <span className="sub-banner__label">{TONE_LABEL[notice.tone]}:</span> {notice.title}
        </p>
        <p className="sub-banner__message">{notice.message}</p>
      </div>
      <div className="sub-banner__actions">
        {notice.actions.map((action) => (
          <Link
            key={action.label}
            to={action.to}
            className={action.primary ? 'sub-banner__cta' : 'sub-banner__link'}
          >
            {action.label}
          </Link>
        ))}
      </div>
    </section>
  );
}
